import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { FiAlertCircle } from 'react-icons/fi';
import { Header } from '../components/layout/Header';
import { isStripeAccountReady, useStripeAccountStatus } from '../api/stripe';
import {
  CreateOfferView,
  type OfferSubmitResult,
} from '../components/gigs/my-gigs/offers/CreateOfferView';
import { ProfileGuard } from '../components/auth/ProfileGuard';
import { useOffer } from '../api/offers';

export type CreateOfferSearch = {
  recipientEmail?: string;
  recipientName?: string;
  recipientUserId?: number;
  conversationId?: string;
  title?: string;
  description?: string;
  applicationUuid?: string;
  gigUuid?: string;
  predecessorOfferUuid?: string;
};

export const Route = createFileRoute('/offers/create')({
  component: CreateOfferPage,
  validateSearch: (search: Record<string, unknown>): CreateOfferSearch => ({
    recipientEmail: typeof search.recipientEmail === 'string' ? search.recipientEmail : undefined,
    recipientName: typeof search.recipientName === 'string' ? search.recipientName : undefined,
    recipientUserId:
      search.recipientUserId != null && search.recipientUserId !== ''
        ? Number(search.recipientUserId)
        : undefined,
    conversationId: typeof search.conversationId === 'string' ? search.conversationId : undefined,
    title: typeof search.title === 'string' ? search.title : undefined,
    description: typeof search.description === 'string' ? search.description : undefined,
    applicationUuid:
      typeof search.applicationUuid === 'string' ? search.applicationUuid : undefined,
    gigUuid: typeof search.gigUuid === 'string' ? search.gigUuid : undefined,
    predecessorOfferUuid:
      typeof search.predecessorOfferUuid === 'string' ? search.predecessorOfferUuid : undefined,
  }),
});

function goAfterOfferSubmit(
  navigate: ReturnType<typeof useNavigate>,
  result: OfferSubmitResult | undefined,
  fallback: () => void,
) {
  if (result && result.status !== 'draft') {
    if (result.conversationId) {
      navigate({
        to: '/messages',
        search: {
          conversationUuid: result.conversationId,
          offerUuid: result.offerId,
        },
      });
    } else {
      navigate({ to: '/offers/$offerId', params: { offerId: result.offerId } });
    }
    return;
  }
  fallback();
}

function CreateOfferPage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const predecessorQuery = useOffer(search.predecessorOfferUuid, {
    enabled: Boolean(search.predecessorOfferUuid),
  });

  const defaults = {
    recipientEmail: search.recipientEmail,
    recipientName: search.recipientName,
    recipientUserId:
      search.recipientUserId != null && !Number.isNaN(search.recipientUserId)
        ? search.recipientUserId
        : undefined,
    conversationId: search.conversationId,
    title: search.title,
    description: search.description,
    applicationUuid: search.applicationUuid,
    gigUuid: search.gigUuid,
    predecessorOfferUuid: search.predecessorOfferUuid,
  };

  const backTab = search.applicationUuid ? 'applicants' : 'offers';
  const predecessor = predecessorQuery.data;
  const isDirectOffer = !(search.gigUuid || predecessor?.gigUuid);
  const { data: stripeAccount, isLoading: stripeLoading } = useStripeAccountStatus();
  const stripeBlocked = isDirectOffer && !stripeLoading && !isStripeAccountReady(stripeAccount);

  const mergedDefaults = predecessor
    ? {
        ...defaults,
        recipientEmail: defaults.recipientEmail || predecessor.recipientEmail || undefined,
        recipientName: defaults.recipientName || predecessor.recipientName || undefined,
        recipientUserId: defaults.recipientUserId ?? predecessor.recipientUserId ?? undefined,
        conversationId: defaults.conversationId || predecessor.conversationId || undefined,
        title: defaults.title || predecessor.title,
        description: defaults.description || predecessor.description,
        gigUuid: defaults.gigUuid || predecessor.gigUuid || undefined,
        predecessorOfferUuid: search.predecessorOfferUuid,
      }
    : defaults;

  return (
    <div className="min-h-screen bg-black text-white pb-28">
      <Header />
      <ProfileGuard>
        <main className="mx-auto max-w-[1360px] px-4 sm:px-6 lg:px-8 pt-[calc(var(--header-height,5.5rem)+1.75rem)]">
          {search.predecessorOfferUuid && predecessorQuery.isLoading ? (
            <p className="text-sm text-zinc-400">Loading the previous offer…</p>
          ) : isDirectOffer && stripeLoading ? (
            <p className="text-sm text-zinc-400">Checking your payment setup…</p>
          ) : stripeBlocked ? (
            <div className="mx-auto mt-10 max-w-md rounded-3xl border border-[#2C2C2E] bg-[#1C1C1E] p-6 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-500/10 text-amber-500">
                <FiAlertCircle size={32} />
              </div>
              <p className="mb-2 font-medium text-zinc-200">
                Please complete your Stripe onboarding before creating an offer.
              </p>
              <p className="mb-6 text-sm leading-relaxed text-zinc-400">
                Recipients pay direct offers through Stripe checkout links, so you need a connected
                Stripe account to receive the payment.
              </p>
              <div className="flex flex-col gap-3">
                <Link
                  to="/profile/settings/payments"
                  className="inline-flex w-full items-center justify-center rounded-full bg-[#1784FF] px-5 py-3 text-sm font-semibold text-white shadow-lg transition hover:bg-blue-600"
                >
                  Go to Stripe Onboarding
                </Link>
                <button
                  type="button"
                  onClick={() => navigate({ to: '/profile/my-gigs', search: { tab: backTab } })}
                  className="mt-1 cursor-pointer text-xs font-semibold text-zinc-500 transition hover:text-zinc-300"
                >
                  Back to Offers
                </button>
              </div>
            </div>
          ) : (
            <CreateOfferView
              key={predecessor?.id || 'new-offer'}
              defaults={mergedDefaults}
              initialOffer={predecessor}
              onBack={() => navigate({ to: '/profile/my-gigs', search: { tab: backTab } })}
              onSubmitSuccess={(result) =>
                goAfterOfferSubmit(navigate, result, () =>
                  navigate({ to: '/profile/my-gigs', search: { tab: 'offers' } }),
                )
              }
            />
          )}
        </main>
      </ProfileGuard>
    </div>
  );
}
